import InfoOutlined from "@mui/icons-material/InfoOutlined";
import type { Editor } from "@tiptap/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { type ConditionExpr, formatCondition } from "./conditional";
import { ConditionEditor } from "./conditional-node";
import type { DynamicNodeOption } from "./dynamic-node-context";
import { useDynamicNodeContext } from "./dynamic-node-context";
import { DynamicNodePopover } from "./dynamic-node-popover";
import "./dynamic-nodes.css";
import { collectReferencedKeys } from "./resolution";
import { parseRowCondition, wrapRowCondition } from "./row-condition";
import { getSlashDataKeyItems } from "./slash-command/data-key-registry";

interface RowConditionPopoverProps {
  editor: Editor;
  /** Absolute document position of the `tableRow` node. */
  rowPos: number;
  /** Viewport rect of the menu trigger the popover anchors below. */
  anchorRect: DOMRect;
  onClose: () => void;
}

/**
 * View/edit the condition of a single conditional table row. Reuses the
 * conditional node's `ConditionEditor` (a row condition is just a single boolean
 * expression — no branch bodies, since the row itself is the body) and writes the
 * formatted expression straight back onto `tableRow.attrs.condition`. Mirrors the
 * conditional chip's popover (portal to body, click-outside / scroll to close).
 */
export const RowConditionPopover: React.FC<RowConditionPopoverProps> = ({
  editor,
  rowPos,
  anchorRect,
  onClose,
}) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const anchorEl = useMemo(
    () => ({ getBoundingClientRect: () => anchorRect }),
    [anchorRect]
  );
  const nodeId = useId();
  // The colour legend is hidden by default; the "i" toggle reveals it — matching
  // the conditional chip's popover. (The row's left-edge bar always shows colour.)
  const [showInfo, setShowInfo] = useState(false);
  const { contextKeys, registerKeys, unregisterKeys } = useDynamicNodeContext();

  // Read the live condition off the node each render so external edits show.
  const condition =
    (editor.state.doc.nodeAt(rowPos)?.attrs.condition as string | null) ?? "";

  // An empty/unset condition (adding one to a row) starts in the variable picker
  // rather than the raw-expression fallback, so authors get the Context / Data key
  // dropdowns straight away.
  const expr = useMemo<ConditionExpr>(
    () =>
      condition.trim() === ""
        ? { kind: "var", variable: "" }
        : parseRowCondition(condition),
    [condition]
  );
  const referencedKeys = useMemo(
    () => collectReferencedKeys(wrapRowCondition(condition)),
    [condition]
  );

  // Pull this row's keys into the provider's batched request while open, so a
  // newly-referenced data key actually gets fetched and the status is accurate.
  useEffect(() => {
    registerKeys(nodeId, referencedKeys);
    return () => unregisterKeys(nodeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  // Close on outside click or scroll (the anchor handle unmounts with the menu,
  // so the snapshot rect would otherwise drift).
  useEffect(() => {
    const onDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!target.isConnected || popoverRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const dataKeyOptions = useMemo<DynamicNodeOption[]>(
    () => getSlashDataKeyItems().map((item) => ({ key: item.key })),
    []
  );

  const commit = (next: ConditionExpr): void => {
    const formatted = formatCondition(next).trim();
    const node = editor.state.doc.nodeAt(rowPos);
    if (!node || node.type.name !== "tableRow") {
      return;
    }
    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(rowPos, undefined, {
        ...node.attrs,
        // An empty expression would serialize to a broken `{{#if }}`; treat it as
        // "no condition" so the row simply stops being conditional.
        condition: formatted === "" ? null : formatted,
      })
    );
  };

  return (
    <DynamicNodePopover
      ref={popoverRef}
      anchorEl={anchorEl}
      className="tiptap-conditional-popover formly-row-condition-popover"
    >
      <div className="tiptap-conditional-popover__header">
        <span>Row condition</span>
        <button
          type="button"
          className={
            showInfo
              ? "tiptap-conditional-popover__info is-active"
              : "tiptap-conditional-popover__info"
          }
          title="Show resolution status"
          aria-pressed={showInfo}
          onMouseDown={(event) => {
            event.preventDefault();
            setShowInfo((value) => !value);
          }}
        >
          <InfoOutlined fontSize="inherit" />
        </button>
      </div>
      {showInfo ? (
        <div className="tiptap-conditional-popover__legend">
          <span className="tiptap-conditional-popover__legend-item">
            <span className="tiptap-conditional-popover__swatch tiptap-conditional-popover__swatch--resolved" />
            conditional value resolved
          </span>
          <span className="tiptap-conditional-popover__legend-item">
            <span className="tiptap-conditional-popover__swatch tiptap-conditional-popover__swatch--loading" />
            conditional value loading
          </span>
          <span className="tiptap-conditional-popover__legend-item">
            <span className="tiptap-conditional-popover__swatch tiptap-conditional-popover__swatch--unresolved" />
            conditional value not found
          </span>
          <span className="tiptap-conditional-popover__legend-note">
            If the row is dimmed, the value is false, and it won&apos;t show up
            in the final document.
          </span>
        </div>
      ) : null}

      <div className="tiptap-conditional-branch">
        <ConditionEditor
          condition={expr}
          contextOptions={contextKeys}
          dataKeyOptions={dataKeyOptions}
          onChange={commit}
        />
      </div>
    </DynamicNodePopover>
  );
};

export default RowConditionPopover;
